import { CheckOutlineIcon } from '@ikyucom/design-system-icons-react'
import { CheckBox, InputText, RadioButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { twMerge } from 'tailwind-merge'
import { DangerousHtml } from '~/components/dangerousHtml/dangerousHtml'
import Image from '~/components/image/Image'
import { useYahooConnectUrl } from '~/features/reservationInput/lib/url'
import { PAYPAY_IMAGE_URL, YAHOO_IMAGE_URL } from '~/lib/constants/constants'
import { UnknownTypeError } from '~/lib/error/unknownTypeError'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import { Button } from '~/ui'
import {
  useCheckIsPayPayPointEnabled,
  useDisplayIdentification,
  useIsPayPayVisible,
  usePayPayBalance,
} from '../../store/payment'
import { useIsPayPayPointEnabled } from '../../store/stateMachine/context'
import { useReserveInfo } from '../../store/stateMachine/state'
import { useContents } from './useContents'
import { useInput } from './useInput'

const styles = sva({
  slots: ['container', 'head', 'title', 'content'],
  base: {
    container: {
      bg: 'white',
      paddingX: '3',
      paddingY: '4',
      _pc: {
        padding: '6',
      },
    },
    head: {
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: 'bold',
    },
    title: {
      fontSize: 'xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
      },
    },
    content: {
      mt: '3',
      spaceY: '4',
      _pc: {
        w: '[424px]',
        ml: 'auto',
        border: 'medium',
        borderRadius: 'default',
        p: '4',
      },
    },
  },
})

export function PayPayPointsUsage() {
  const isPC = useIsPC()
  const PayPayPointsUsage_ = isPC ? PcPayPayPointsUsage : SdPayPayPointsUsage
  return <PayPayPointsUsage_ />
}

function PcPayPayPointsUsage() {
  const annotation = useDisplayIdentification()
  const isVisible = useIsPayPayVisible()
  const classes = styles()
  if (!isVisible) {
    return null
  }

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h2 className={classes.title}>PayPay（残高）等利用</h2>
        <div>
          {annotation !== '' && (
            <div className="flex space-x-1">
              <div
                className={twMerge(
                  'flex size-3.5 items-center justify-center rounded-full',
                  annotation === '本人確認済' ? css({ bg: 'success' }) : 'bg-neutral-400'
                )}
              >
                <CheckOutlineIcon className="text-neutral-000" width={10} height={10} />
              </div>
              <div className={css({ fontSize: 'xs', fontWeight: 'normal' })}>{annotation}</div>
            </div>
          )}
        </div>
      </div>
      <Contents />
    </div>
  )
}

function SdPayPayPointsUsage() {
  const annotation = useDisplayIdentification()
  const isVisible = useIsPayPayVisible()
  const classes = styles()
  if (!isVisible) {
    return null
  }

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h2 className="text-xl font-bold">PayPay（残高）等利用</h2>
        {annotation !== '' && (
          <div className="flex space-x-1">
            <div
              className={twMerge(
                'flex size-3.5 items-center justify-center rounded-full',
                annotation === '本人確認済' ? css({ bg: 'success' }) : 'bg-neutral-400'
              )}
            >
              <CheckOutlineIcon className="text-neutral-000" width={10} height={10} />
            </div>
            <div className={css({ fontSize: 'xs', fontWeight: 'normal' })}>{annotation}</div>
          </div>
        )}
      </div>
      <Contents />
    </div>
  )
}

function Contents() {
  const { messageType, payPayConnectUrl, payPayAuthenticationUrl } = useContents()
  switch (messageType) {
    case 'hasPaypayMaintenanceMessage':
      return <PayPayMaintenance />
    case 'needsYahooIdConnection':
      return <NeedsYahooIDConnection />
    case 'needsPayPayRegistration':
      return (
        <Unavailable
          text="PayPay残高を利用するには、PayPayの登録が必要です。"
          label="PayPayに登録する"
          href={payPayConnectUrl()}
        />
      )
    case 'needsPhoneNumberAuthentication':
      return (
        <Unavailable
          text="PayPayを安全にご利用いただくために携帯電話番号の認証が必要です。"
          label="携帯電話番号の登録"
          href={payPayAuthenticationUrl()}
        />
      )
    case 'invalidYahooIdToken':
      return <ErrorMessage message="エラーのため、PayPay（残高）等を利用できません。" />
    case 'invalidPayPayBalance':
      return (
        <ErrorMessage message="PayPay（残高）等が取得できませんでした。時間をおいて画面を再読み込みしてください。" />
      )
    case 'paypayUnavailable':
      return <ErrorMessage message="この店舗ではPayPay（残高）等はご利用いただけません。" />
    case 'available':
      return <InputPayPayUsage />
    default:
      throw new UnknownTypeError(messageType)
  }
}

function PayPayMaintenance() {
  const fragment = useReserveInfo()?.paypayMaintenanceMessage
  if (fragment === undefined || fragment === '') {
    return null
  }
  return (
    <div className="border border-neutral-300 p-2">
      <DangerousHtml className="paypayMaintenanceText" fragment={fragment} />
    </div>
  )
}

function NeedsYahooIDConnection() {
  const url = useYahooConnectUrl()
  const isPC = useIsPC()
  return (
    <>
      <div
        className={css({
          _pc: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        })}
      >
        <p className={css({ mb: '3', fontSize: 'sm', color: 'gray.700', _pc: { mb: '0' } })}>
          ※PayPay（残高）等利用には、
          <a className={css({ color: 'link' })} href={url()}>
            Yahoo! JAPAN ID
          </a>
          との連携が必要です。
        </p>
        <a href={url()} className="block">
          <Button
            color="normal"
            size="xs"
            spread={isPC ? false : true}
            leftIcon={<Image src={YAHOO_IMAGE_URL} width={16} height={16} alt="" />}
          >
            Yahoo! JAPAN IDと連携する
          </Button>
        </a>
      </div>
    </>
  )
}

function Unavailable({ text, label, href }: { text: string; label: string; href: string }) {
  const isPC = useIsPC()
  return (
    <>
      <div
        className={css({
          _pc: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        })}
      >
        <p className={css({ mb: '3', color: 'gray.700', _pc: { mb: '0' } })}>{text}</p>
        <a href={href} className="block">
          <Button
            color="normal"
            size="xs"
            spread={isPC ? false : true}
            leftIcon={<Image src={PAYPAY_IMAGE_URL} width={12} height={12} alt="" />}
          >
            {label}
          </Button>
        </a>
      </div>
    </>
  )
}

function ErrorMessage({ message }: { message: string }) {
  return <p className={css({ fontSize: 'sm', color: 'error.text' })}>{message}</p>
}

function InputPayPayUsage() {
  const { enabled, makeOnChange } = useInputPayPayUsage()
  const classes = styles()
  return (
    <>
      <Balance />
      <div className={classes.content}>
        <RadioButton name="paypayPointsEnabled" checked={!enabled} onChange={makeOnChange(false)}>
          利用しない
        </RadioButton>
        <div className="space-y-2">
          <RadioButton name="paypayPointsEnabled" checked={enabled} onChange={makeOnChange(true)}>
            利用する
          </RadioButton>
          <Input />
          <PayPayNotice />
        </div>
      </div>
    </>
  )
}

function useInputPayPayUsage() {
  const enabled = useIsPayPayPointEnabled()
  const change = useCheckIsPayPayPointEnabled()
  const makeOnChange = useCallback(
    (v: boolean) => {
      return () => change(v)
    },
    [change]
  )
  return { enabled, makeOnChange }
}

function Balance() {
  const balance = usePayPayBalance()
  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        fontSize: 'sm',
        mt: '-2',
      })}
    >
      <p className="flex items-center">
        <Image src={PAYPAY_IMAGE_URL} width={12} height={12} alt="PayPay" className="mr-1" />
        利用可能額：
      </p>
      <span className={css({ fontWeight: 'bold', fontSize: 'lg' })}>{money(balance)}円</span>
    </div>
  )
}

function Input() {
  const { usage, onChange, onCheck, checked, enabled } = useInput()
  if (!enabled) {
    return null
  }
  return (
    <div className="ml-7 space-y-2 rounded bg-neutral-100 p-3">
      <div className="space-x-2">
        <InputText
          value={usage}
          disabled={checked}
          width={110}
          inputMode="numeric"
          onChange={onChange}
        />
        <span>円分利用</span>
      </div>
      <CheckBox label="すべて利用" onChange={onCheck} checked={checked} />
    </div>
  )
}

function PayPayNotice() {
  const fragment = useReserveInfo()?.paypayNoticeMessage
  if (fragment === undefined || fragment === '') {
    return null
  }
  return (
    <div className="border border-neutral-300 p-2">
      <DangerousHtml className="paypayMaintenanceText" fragment={fragment} />
    </div>
  )
}
